/**
 * Browser-side file helpers for the card actions: save a vCard, export the
 * QR as SVG or PNG, hand an image of the card to the native share sheet.
 *
 * Everything runs in the browser from data the page already has; nothing is
 * fetched from a third party.
 */

import type { CardProfile } from "@/lib/api";
import { shareText } from "@/lib/card-face";
import { cardImageBlob } from "@/lib/card-image";
import { logger } from "@/lib/logger";
import { encodeQr, qrToSvg } from "@/lib/qr";

/** Norican Blue, the only colour a QR is exported in (brand guideline palette). */
const QR_DARK = "#03193d";
const PNG_SIZE_PX = 1024;

/** Trigger a download of `blob` under `filename`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so the click has started the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A vCard as a downloadable file. */
export function vcardFile(vcard: string, name: string): File {
  return new File([vcard], `${safeFilename(name)}.vcf`, { type: "text/vcard;charset=utf-8" });
}

/** The card's QR as a standalone SVG file. */
export function qrSvgBlob(vcard: string): Blob {
  const svg = qrToSvg(encodeQr(vcard, "M"), { dark: QR_DARK, quietZone: 4 });
  return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
}

/** The card's QR rasterised to a 1024px PNG via an off-screen canvas. */
export async function qrPngBlob(vcard: string): Promise<Blob> {
  const svg = qrToSvg(encodeQr(vcard, "M"), {
    dark: QR_DARK,
    quietZone: 4,
    sizePx: PNG_SIZE_PX,
  });
  const image = new Image();
  const svgUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Could not rasterise the QR code"));
      image.src = svgUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = PNG_SIZE_PX;
    canvas.height = PNG_SIZE_PX;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas is not available");
    }
    context.drawImage(image, 0, 0, PNG_SIZE_PX, PNG_SIZE_PX);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("PNG export failed"))),
        "image/png",
      );
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/** The front of the card as a PNG file (see card-image.ts). */
export async function cardImageFile(card: CardProfile): Promise<File> {
  return new File([await cardImageBlob(card)], `${safeFilename(card.display_name)}-card.png`, {
    type: "image/png",
  });
}

/**
 * Hand the card image and the contact lines to the native share sheet
 * (phones, Windows), falling back to downloading the image where the browser
 * cannot share files. Returns which path ran.
 *
 * An image is what every messenger and mail app takes; a bare .vcf was
 * offered only "open" or "save" in Teams on iOS (October 2026). Safari lets
 * `navigator.share` run only straight from the tap, so pass `image` rendered
 * ahead of time: rendering it here costs the tap its user activation on iOS
 * and ends in the download.
 */
export async function shareCard(
  card: CardProfile,
  image: File | null,
): Promise<"shared" | "downloaded"> {
  const file = image ?? (await cardImageFile(card));
  const data: ShareData = {
    files: [file],
    text: shareText(card),
    title: `${card.display_name}, Norican`,
  };
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (typeof nav.share === "function" && nav.canShare?.(data)) {
    try {
      await nav.share(data);
      return "shared";
    } catch (err) {
      // AbortError is the user closing the sheet; anything else falls back.
      if ((err as { name?: string }).name === "AbortError") {
        return "shared";
      }
      logger.warn("[card] native share failed, downloading instead:", err);
    }
  }
  downloadBlob(file, file.name);
  return "downloaded";
}

/** Keep filenames portable across OSes and mail clients. */
export function safeFilename(name: string): string {
  const cleaned = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cleaned || "contact";
}
